import React, { useState } from 'react';

/**
 * A custom tooltip component that appears immediately on hover with customizable styling
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The element that triggers the tooltip
 * @param {string} props.content - The content to display in the tooltip
 * @param {string} props.className - Optional additional CSS classes for the tooltip
 * @param {string} props.position - Optional position of the tooltip ('top', 'bottom', 'left', 'right'). Default is 'top'.
 * @param {string} props.minWidth - Optional minimum width for the tooltip. Default is '400px'.
 * @param {string} props.maxWidth - Optional maximum width for the tooltip. Default is '600px'.
 * @returns {JSX.Element} - The rendered component
 */
const CustomTooltip = ({ children, content, className = '', position = 'top', minWidth = '400px', maxWidth = '600px' }) => {
  const [isVisible, setIsVisible] = useState(false);

  // Handle mouse events
  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);

  return (
    <div 
      className="inline-block relative"
      style={{ overflow: 'visible' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      {isVisible && content && (
        <div
          className={`absolute z-[9999] bg-gray-900 text-white px-4 py-2 rounded shadow-lg text-base ${className}`}
          style={{
            ...(position === 'top' && {
              bottom: 'calc(100% + 10px)',
              left: '50%',
              transform: 'translateX(-50%)'
            }),
            ...(position === 'bottom' && {
              top: 'calc(100% + 10px)',
              left: '50%',
              transform: 'translateX(-50%)'
            }),
            ...(position === 'left' && {
              right: 'calc(100% + 10px)',
              top: '50%',
              transform: 'translateY(-50%)'
            }),
            ...(position === 'right' && {
              left: 'calc(100% + 10px)',
              top: '50%',
              transform: 'translateY(-50%)'
            }),
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            minWidth,
            maxWidth,
            overflow: 'visible'
          }}
        >
          {content}
          <div 
            className="absolute w-3 h-3 bg-gray-900 transform rotate-45 z-[9999]"
            style={{
              ...(position === 'top' && {
                bottom: '-6px',
                left: '50%',
                marginLeft: '-6px'
              }),
              ...(position === 'bottom' && {
                top: '-6px',
                left: '50%',
                marginLeft: '-6px'
              }),
              ...(position === 'left' && {
                right: '-6px',
                top: '50%',
                marginTop: '-6px'
              }),
              ...(position === 'right' && {
                left: '-6px',
                top: '50%',
                marginTop: '-6px'
              }),
              overflow: 'visible'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CustomTooltip;
